import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import type { ShareableType } from '@bindoc-projects/resource-domain-lib';
import type { BdCondition } from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from './filter-detailed-text';

/**
 * Context passed to toCondition() for plugins that need external data.
 * This replaces the async FilterConditionDependencyResolver pattern.
 */
export interface FilterConditionContext {
  /** Year to use for clinic identifiers */
  year?: number;
}

export type FilterConditionExpression =
  | FilterConditionCombination
  | FilterConditionPlugin<unknown>;

export type FilterConditionExpressionText =
  | string
  | Array<{
      kind: string;
      expressionText: FilterConditionExpressionText;
    }>;

export interface FilterConditionCombination extends FilterConditionCommon<any> {
  type: 'combination';
  kind: 'AND' | 'OR';
  expression: FilterConditionExpression[];

  clone(): FilterConditionCombination;

  overrideClone(override: OverrideProperty[]): FilterConditionCombination;

  equals(other: FilterConditionCombination): boolean;

  kindToText(): string;
}

export interface FilterConditionPlugin<T> extends FilterConditionCommon<T> {
  clone(): FilterConditionPlugin<T>;

  overrideClone(override: OverrideProperty[]): FilterConditionPlugin<T> | null;

  equals(other: FilterConditionPlugin<T>): boolean;

  validationToText(): string;
}

interface FilterConditionCommon<T> {
  type: string;

  toJSON(): unknown;

  /**
   * Converts this filter condition to a BdCondition for API requests.
   * @param context Optional context with year and other parameters
   * @returns The condition or null if invalid
   */
  toCondition(context?: FilterConditionContext): BdCondition | null;

  toText(): string | null;

  toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null;

  hasRelativeTimeSelection(): boolean;

  getChildResources(): { [resourceName in ShareableType]?: string[] };

  getExtraChildResources(): Record<string, string[]>;

  getOverrideProperties(
    tileId: string,
    dependencyVirtualClinicId: string,
  ): OverrideProperty[];

  valid(): boolean;
}

export function expressionPluginLookup<V extends FilterConditionPlugin<any>>(
  expression: FilterConditionExpression,
  key: string,
  options?: { depth?: number; occurrence?: number },
): V | undefined {
  const _options: { depth: number; occurrence: number } = {
    depth: options?.depth ?? 0,
    occurrence: options?.occurrence ?? 0,
  };

  if (expression.type === key) {
    // depth === 0 means final depth reached
    if (_options.occurrence === 0 && _options.depth === 0) {
      return expression as V;
    } else {
      return undefined;
    }
  } else if (expression.type === 'combination') {
    // depth === 0 means final depth reached
    if (_options.depth === 0) {
      return (expression as FilterConditionCombination).expression
        .filter((it) => it.type === key)
        .at(_options.occurrence) as V;
    } else {
      const nestedCombinationExp = (
        expression as FilterConditionCombination
      ).expression.find((it) => it.type === 'combination');
      return expressionPluginLookup(nestedCombinationExp, key, {
        depth: Math.max(...[_options.depth - 1, 0]),
        occurrence: _options.occurrence,
      });
    }
  } else {
    return undefined;
  }
}
